import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "./ui/alert-dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Truck, PackageCheck, Loader2, ScanBarcode, Zap, CheckCircle2, AlertTriangle,
  FileText, Camera, MessageSquarePlus, ClipboardEdit, Warehouse, Paperclip, ChevronDown,
  ChevronUp, Image as ImageIcon, ScanText,
} from "lucide-react";
import BarcodeScannerDialog from "./BarcodeScannerDialog";
import PartNumberCameraDialog from "./PartNumberCameraDialog";
import { broadcast } from "../lib/sync";

const statusChip = (status) => {
  if (status === "received") return "bg-emerald-100 text-emerald-800";
  if (status === "over") return "bg-amber-100 text-amber-900";
  if (status === "partial") return "bg-blue-100 text-blue-800";
  if (status === "extra") return "bg-purple-100 text-purple-800";
  return "bg-slate-100 text-slate-700";
};

/**
 * Receive a specific expected off-site delivery.
 * Two receive modes:
 *   - Standard: type/scan a SKU, enter Qty, press Receive
 *   - Fast Scan: continuous camera. Each scan = +1 automatically.
 * Overage handling: server returns 409 { detail:"overage", ... } when the
 * requested qty would exceed expected. We confirm with the user and retry.
 */
export default function ReceiveOffSiteDialog({ open, onOpenChange, delivery, onChanged }) {
  const [current, setCurrent] = useState(delivery);
  const [sku, setSku] = useState("");
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [fastScanOpen, setFastScanOpen] = useState(false);
  const [flashId, setFlashId] = useState(null);
  const [pendingOverage, setPendingOverage] = useState(null); // {sku, qty, message}
  const [expandedLineId, setExpandedLineId] = useState(null);
  const [noteDraft, setNoteDraft] = useState({}); // {lineId: text}
  const [moveTarget, setMoveTarget] = useState(null); // {line, qty}
  const [partCameraOpen, setPartCameraOpen] = useState(false);
  const [fastScanMode, setFastScanMode] = useState("ocr"); // "ocr" | "barcode"
  const skuInputRef = useRef(null);
  const receivingPaperRef = useRef(null);
  const photoRef = useRef(null);
  const linePhotoRef = useRef({});

  useEffect(() => {
    if (open && delivery) {
      setCurrent(delivery);
      setSku("");
      setQty("");
      setTimeout(() => skuInputRef.current?.focus(), 100);
    }
  }, [open, delivery?.id]);

  // Broadcast every successful mutation so other windows (main dashboard,
  // Off-Site Deliveries list, popped-out views) refetch and stay in sync.
  useEffect(() => {
    if (!current?.id || !current?.updated_at) return;
    broadcast("delivery-updated", {
      deliveryId: current.id,
      projectId: current.project_id,
      updatedAt: current.updated_at,
    });
  }, [current?.id, current?.updated_at]);

  const refresh = async () => {
    if (!current?.id) return;
    try {
      const { data } = await api.get(`/expected-deliveries/${current.id}`);
      setCurrent(data);
      onChanged?.(data);
    } catch {
      /* ignore */
    }
  };

  const flash = (id) => {
    setFlashId(id);
    setTimeout(() => setFlashId(null), 1000);
  };

  // Client-side match hint (visual preview only)
  const match = useMemo(() => {
    const s = sku.trim().toUpperCase();
    if (!s || !current) return null;
    return current.lines?.find(
      (l) => !l.unexpected && l.sku.toUpperCase() === s
    );
  }, [sku, current]);

  const project_id = current?.project_id;

  const callScan = async (rawSku, n, allowOverage = false) => {
    const body = {
      project_id,
      sku: rawSku,
      quantity: n,
      allow_overage: allowOverage,
    };
    try {
      const { data } = await api.post("/expected-deliveries/scan", body);
      setCurrent(data.delivery);
      onChanged?.(data.delivery);
      if (data.matched_line_id) flash(data.matched_line_id);
      const line = data.delivery.lines.find((l) => l.id === data.matched_line_id);
      if (data.was_unexpected) {
        toast.warning(`Flagged ${rawSku} as unexpected extra (${n})`);
      } else {
        const remaining = line ? line.remaining : 0;
        toast.success(
          `+${n} ${line?.item_name || rawSku}${remaining > 0 ? ` · ${remaining} still expected` : " · fully received"}`
        );
      }
      return { ok: true };
    } catch (err) {
      const detail = err.response?.data;
      if (err.response?.status === 409 && detail?.detail === "overage") {
        return { overage: detail };
      }
      toast.error(detail?.detail || "Could not record scan");
      return { ok: false };
    }
  };

  const doReceive = async (scannedSku, options = {}) => {
    const rawSku = String(scannedSku || sku).trim().toUpperCase();
    if (!rawSku) {
      toast.error("Enter or scan a SKU");
      return;
    }
    const n = Number(qty) > 0 ? Number(qty) : (options.autoQty || 1);
    setBusy(true);
    try {
      const res = await callScan(rawSku, n, false);
      if (res.overage) {
        setPendingOverage({ ...res.overage, quantity: n });
        return;
      }
      if (res.ok) {
        setSku("");
        setQty("");
        skuInputRef.current?.focus();
      }
    } finally {
      setBusy(false);
    }
  };

  const acceptOverage = async () => {
    if (!pendingOverage) return;
    setBusy(true);
    try {
      const res = await callScan(pendingOverage.sku, pendingOverage.quantity, true);
      if (res.ok) {
        setSku("");
        setQty("");
        setPendingOverage(null);
        skuInputRef.current?.focus();
      }
    } finally {
      setBusy(false);
    }
  };

  const handleStandardScan = (code) => {
    setSku(code);
    setTimeout(() => doReceive(code), 30);
  };

  // Fast scan mode: every camera detection increments by 1, no manual step.
  const fastScanHandler = async (code) => {
    if (!project_id) return;
    // Silent overage — we still respect the 409, but auto-prompt afterwards.
    const rawSku = String(code || "").trim().toUpperCase();
    if (!rawSku) return;
    const res = await callScan(rawSku, 1, false);
    if (res.overage) {
      setPendingOverage({ ...res.overage, quantity: 1 });
      // Close fast scan so the confirm dialog is visible
      setFastScanOpen(false);
    }
  };

  const paperwork = current?.original_paperwork || [];
  const receivingPaperwork = current?.receiving_paperwork || [];
  const photos = current?.photos || [];

  const paperworkUrl = (att) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    return `${base}/api/offsite-deliveries/paperwork/${att.id}/download`;
  };

  const uploadReceivingPaperwork = async (file) => {
    if (!file || !current) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("File exceeds 20 MB");
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(
        `/expected-deliveries/${current.id}/receiving-paperwork`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setCurrent(data);
      onChanged?.(data);
      toast.success(`Attached ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not attach paperwork");
    }
  };

  const uploadPhoto = async (file, lineId, caption = "") => {
    if (!file || !current) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("Photo exceeds 20 MB");
    const form = new FormData();
    form.append("file", file);
    const qs = new URLSearchParams();
    if (lineId) qs.set("line_id", lineId);
    if (caption) qs.set("caption", caption);
    try {
      const { data } = await api.post(
        `/expected-deliveries/${current.id}/photos${qs.toString() ? `?${qs}` : ""}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setCurrent(data);
      onChanged?.(data);
      toast.success("Photo attached");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not attach photo");
    }
  };

  const submitLineNote = async (lineId) => {
    const text = (noteDraft[lineId] || "").trim();
    if (!text) return;
    try {
      const { data } = await api.post(
        `/expected-deliveries/${current.id}/lines/${lineId}/notes`,
        { text }
      );
      setCurrent(data);
      onChanged?.(data);
      setNoteDraft((d) => ({ ...d, [lineId]: "" }));
      toast.success("Note added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add note");
    }
  };

  const adjustLine = async (lineId, field, value) => {
    try {
      const { data } = await api.post(
        `/expected-deliveries/${current.id}/lines/${lineId}/adjust`,
        { [field]: Math.max(0, Number(value) || 0) }
      );
      setCurrent(data);
      onChanged?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not adjust");
    }
  };

  const doMoveToWarehouse = async () => {
    if (!moveTarget || !current) return;
    const qty = Math.max(1, Number(moveTarget.qty) || 0);
    try {
      const { data } = await api.post(
        `/expected-deliveries/${current.id}/lines/${moveTarget.line.id}/move-to-warehouse`,
        { quantity: qty }
      );
      setCurrent(data);
      onChanged?.(data);
      toast.success(`Moved ${qty} × ${moveTarget.line.item_name} to warehouse`);
      setMoveTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not move to warehouse");
    }
  };

  const damagedTotal = (current?.lines || []).reduce((n, l) => n + (l.damaged_quantity || 0), 0);
  const missingTotal = (current?.lines || []).reduce((n, l) => n + (l.missing_quantity || 0), 0);

  return (
    <>
      <Dialog open={open && !fastScanOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl rounded-md" data-testid="offsite-receive-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="grid place-items-center h-10 w-10 rounded-sm bg-blue-100 text-blue-800">
                <Truck size={20} />
              </div>
              <div className="min-w-0">
                <DialogTitle className="font-display text-2xl font-black tracking-tight truncate">
                  Receive Off-Site Delivery
                </DialogTitle>
                <DialogDescription className="text-slate-600 mt-1">
                  <span className="font-semibold text-slate-900">{current?.project_name}</span>
                  {current?.reference && <> · <span className="font-mono">{current.reference}</span></>}
                  {current?.vendor && <> · {current.vendor}</>}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {current && (
            <div className="space-y-4">
              {/* Paperwork strip: Original + Receiving + Add buttons */}
              <div className="flex flex-wrap gap-2 items-center">
                {paperwork.map((att) => (
                  <a
                    key={att.id}
                    href={paperworkUrl(att)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-blue-800 border border-blue-200 bg-blue-50 rounded-sm px-2 py-1 hover:bg-blue-100"
                    data-testid={`paperwork-link-${att.id}`}
                  >
                    <FileText size={12} /> {att.original_filename}
                  </a>
                ))}
                {receivingPaperwork.map((att) => (
                  <a
                    key={att.id}
                    href={paperworkUrl(att)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-emerald-800 border border-emerald-200 bg-emerald-50 rounded-sm px-2 py-1 hover:bg-emerald-100"
                    data-testid={`receiving-paperwork-link-${att.id}`}
                  >
                    <Paperclip size={12} /> {att.original_filename}
                  </a>
                ))}
                <div className="ml-auto flex gap-2">
                  <input
                    ref={receivingPaperRef}
                    type="file"
                    accept=".pdf,image/*"
                    className="hidden"
                    onChange={(e) => uploadReceivingPaperwork(e.target.files?.[0])}
                    data-testid="receiving-paper-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => receivingPaperRef.current?.click()}
                    className="rounded-sm text-xs"
                    data-testid="add-receiving-paperwork-btn"
                  >
                    <Paperclip size={12} className="mr-1" /> Add Receiving Paperwork
                  </Button>
                  <input
                    ref={photoRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => uploadPhoto(e.target.files?.[0], null)}
                    data-testid="delivery-photo-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => photoRef.current?.click()}
                    className="rounded-sm text-xs"
                    data-testid="add-delivery-photo-btn"
                  >
                    <Camera size={12} className="mr-1" /> Add Photo
                  </Button>
                </div>
              </div>

              {/* Damaged / Missing / Photos summary */}
              {(damagedTotal > 0 || missingTotal > 0 || photos.length > 0) && (
                <div className="flex flex-wrap gap-2 text-[11px]">
                  {damagedTotal > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-red-100 text-red-800 font-semibold uppercase tracking-widest">
                      <AlertTriangle size={11} /> {damagedTotal} damaged
                    </span>
                  )}
                  {missingTotal > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-amber-100 text-amber-900 font-semibold uppercase tracking-widest">
                      <AlertTriangle size={11} /> {missingTotal} missing
                    </span>
                  )}
                  {photos.length > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-slate-100 text-slate-700 font-semibold uppercase tracking-widest">
                      <ImageIcon size={11} /> {photos.length} photo{photos.length === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              )}

              {/* Receive controls */}
              <div className="bg-slate-50 border border-slate-200 rounded-md p-4 grid grid-cols-12 gap-3 items-end">
                <div className="col-span-12 md:col-span-6">
                  <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">
                    Part / SKU
                  </Label>
                  <div className="mt-1.5 flex gap-2">
                    <Input
                      ref={skuInputRef}
                      value={sku}
                      onChange={(e) => setSku(e.target.value)}
                      placeholder="Type or scan"
                      data-testid="offsite-sku-input"
                      className="rounded-sm font-mono uppercase h-11"
                      onKeyDown={(e) => { if (e.key === "Enter") doReceive(); }}
                    />
                    <Button
                      type="button"
                      onClick={() => setPartCameraOpen(true)}
                      data-testid="offsite-scan-btn"
                      className="rounded-sm h-11 bg-blue-800 hover:bg-blue-900 text-white shrink-0"
                    >
                      <ScanText size={18} className="mr-1.5" /> Scan Part #
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setScannerOpen(true)}
                      data-testid="offsite-barcode-btn"
                      variant="outline"
                      className="rounded-sm h-11 shrink-0"
                      title="Scan a real barcode (rare)"
                    >
                      <ScanBarcode size={16} />
                    </Button>
                  </div>
                  {sku.trim() && match && (
                    <div className="mt-1 text-xs text-slate-600">
                      Matches <span className="font-semibold">{match.item_name}</span> ·
                      current <span className="font-mono">{match.received_quantity}/{match.expected_quantity}</span>
                    </div>
                  )}
                </div>
                <div className="col-span-4 md:col-span-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Qty</Label>
                  <Input
                    type="number"
                    min="1"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    placeholder="1"
                    data-testid="offsite-qty-input"
                    className="mt-1.5 rounded-sm font-mono h-11"
                  />
                </div>
                <div className="col-span-8 md:col-span-2">
                  <Button
                    onClick={() => doReceive()}
                    disabled={busy || !sku.trim()}
                    data-testid="offsite-receive-btn"
                    className="w-full h-11 rounded-sm bg-blue-800 hover:bg-blue-900 text-white font-semibold"
                  >
                    {busy ? <Loader2 size={16} className="animate-spin mr-1" /> : <PackageCheck size={16} className="mr-1" />}
                    Receive
                  </Button>
                </div>
                <div className="col-span-12 md:col-span-2">
                  <Button
                    onClick={() => { setFastScanMode("ocr"); setFastScanOpen(true); }}
                    variant="outline"
                    data-testid="offsite-fast-scan-btn"
                    className="w-full h-11 rounded-sm border-blue-300 text-blue-800 hover:bg-blue-50 font-semibold"
                  >
                    <Zap size={16} className="mr-1" /> Fast Scan
                  </Button>
                </div>
              </div>

              {/* Lines list */}
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 flex items-center justify-between">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    Expected Items
                  </div>
                  <div className="text-[10px] font-mono text-slate-500">
                    {current.totals?.received || 0} / {current.totals?.expected || 0} received
                    {current.totals?.remaining ? ` · ${current.totals.remaining} remaining` : ""}
                  </div>
                </div>
                <ul className="divide-y divide-slate-200 max-h-[42vh] overflow-y-auto">
                  {(current.lines || []).map((l) => {
                    const expanded = expandedLineId === l.id;
                    const linePhotos = photos.filter((p) => p.line_id === l.id);
                    const noteEntries = l.notes_log || [];
                    return (
                      <li
                        key={l.id}
                        className={`transition-colors ${flashId === l.id ? "bg-emerald-50" : ""}`}
                        data-testid={`offsite-line-${l.id}`}
                      >
                        <div className="px-3 py-2.5 flex items-center gap-3">
                          <div className="w-5 shrink-0">
                            {l.status === "received" || l.status === "over" || l.status === "extra" ? (
                              <CheckCircle2 size={18} className={l.status === "extra" ? "text-purple-600" : "text-emerald-600"} />
                            ) : (
                              <div className="h-4 w-4 border-2 border-slate-300 rounded-sm" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-sm font-semibold text-slate-900">{l.sku}</span>
                              <span className="text-sm text-slate-700 truncate">{l.item_name}</span>
                              {l.unexpected && (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm bg-purple-100 text-purple-800">
                                  <AlertTriangle size={9} /> Unexpected
                                </span>
                              )}
                              {(l.damaged_quantity || 0) > 0 && (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm bg-red-100 text-red-800">
                                  {l.damaged_quantity} dmg
                                </span>
                              )}
                              {(l.missing_quantity || 0) > 0 && (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-900">
                                  {l.missing_quantity} miss
                                </span>
                              )}
                              {(l.moved_to_warehouse || 0) > 0 && (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-800">
                                  <Warehouse size={9} /> {l.moved_to_warehouse}
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                              {l.unexpected
                                ? <>received {l.received_quantity} · not on original PO</>
                                : <>received {l.received_quantity} / {l.expected_quantity}{l.remaining > 0 ? ` · ${l.remaining} remaining` : ""}</>
                              }
                            </div>
                          </div>
                          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${statusChip(l.status)}`}>
                            {l.status}
                          </span>
                          <button
                            type="button"
                            onClick={() => setExpandedLineId(expanded ? null : l.id)}
                            aria-label={expanded ? "Collapse" : "Expand"}
                            data-testid={`expand-line-${l.id}`}
                            className="text-slate-400 hover:text-slate-700"
                          >
                            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </div>
                        {expanded && (
                          <div className="px-4 pb-4 pt-1 bg-slate-50 border-t border-slate-200 space-y-3">
                            {/* Damaged / Missing / Move controls */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div>
                                <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Damaged</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  defaultValue={l.damaged_quantity || 0}
                                  onBlur={(e) => adjustLine(l.id, "damaged_quantity", e.target.value)}
                                  data-testid={`damaged-input-${l.id}`}
                                  className="mt-1 rounded-sm h-9 font-mono"
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Missing</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  defaultValue={l.missing_quantity || 0}
                                  onBlur={(e) => adjustLine(l.id, "missing_quantity", e.target.value)}
                                  data-testid={`missing-input-${l.id}`}
                                  className="mt-1 rounded-sm h-9 font-mono"
                                />
                              </div>
                              <div className="flex items-end gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled={(l.received_quantity || 0) <= 0}
                                  onClick={() => setMoveTarget({ line: l, qty: String(l.received_quantity || 1) })}
                                  data-testid={`move-warehouse-btn-${l.id}`}
                                  className="rounded-sm h-9 border-blue-300 text-blue-800 hover:bg-blue-50"
                                >
                                  <Warehouse size={13} className="mr-1.5" /> Move to Warehouse
                                </Button>
                              </div>
                            </div>

                            {/* Photos */}
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex-1">
                                  Photos ({linePhotos.length})
                                </Label>
                                <input
                                  ref={(el) => { if (el) linePhotoRef.current[l.id] = el; }}
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  className="hidden"
                                  onChange={(e) => uploadPhoto(e.target.files?.[0], l.id, "")}
                                  data-testid={`line-photo-input-${l.id}`}
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => linePhotoRef.current[l.id]?.click()}
                                  data-testid={`line-photo-btn-${l.id}`}
                                  className="rounded-sm h-7 text-xs"
                                >
                                  <Camera size={11} className="mr-1" /> Snap
                                </Button>
                              </div>
                              {linePhotos.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                  {linePhotos.map((p) => (
                                    <a
                                      key={p.id}
                                      href={paperworkUrl(p)}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-1 text-[11px] text-slate-700 border border-slate-200 bg-white rounded-sm px-1.5 py-0.5 hover:bg-slate-50"
                                    >
                                      <ImageIcon size={10} /> {p.original_filename}
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Notes */}
                            <div>
                              <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                Notes ({noteEntries.length})
                              </Label>
                              {noteEntries.length > 0 && (
                                <ul className="mt-1 space-y-1 text-xs text-slate-700">
                                  {noteEntries.map((n) => (
                                    <li key={n.id} className="border border-slate-200 bg-white rounded-sm px-2 py-1">
                                      <div>{n.text}</div>
                                      <div className="text-[10px] text-slate-500 mt-0.5">
                                        {n.user_name} · {n.created_at.slice(0, 16).replace("T", " ")}
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              <div className="mt-1.5 flex gap-2">
                                <Textarea
                                  rows={2}
                                  value={noteDraft[l.id] || ""}
                                  onChange={(e) => setNoteDraft((d) => ({ ...d, [l.id]: e.target.value }))}
                                  placeholder="e.g. 2 boxes crushed on top"
                                  data-testid={`note-input-${l.id}`}
                                  className="rounded-sm text-sm"
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => submitLineNote(l.id)}
                                  disabled={!(noteDraft[l.id] || "").trim()}
                                  data-testid={`note-submit-${l.id}`}
                                  className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white h-9"
                                >
                                  <MessageSquarePlus size={13} className="mr-1" /> Add
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { refresh(); onOpenChange(false); }} className="rounded-sm">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Standard barcode scanner (secondary, for real barcoded items). */}
      <BarcodeScannerDialog
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onDetected={handleStandardScan}
        continuous={false}
        title="Scan barcode"
      />

      {/* Fast scan — barcode camera stays on, every read = +1 */}
      <BarcodeScannerDialog
        open={fastScanOpen && fastScanMode === "barcode"}
        onOpenChange={setFastScanOpen}
        onDetected={fastScanHandler}
        continuous={true}
        title="Fast scan · each scan = +1"
      />

      {/* Primary: OCR camera — reads printed part numbers off the box */}
      <PartNumberCameraDialog
        open={partCameraOpen}
        onOpenChange={setPartCameraOpen}
        onDetected={handleStandardScan}
        deliveryId={current?.id}
        projectId={current?.project_id}
        continuous={false}
        title="Scan part number"
      />

      {/* Fast OCR — camera stays on, each read = +1 */}
      <PartNumberCameraDialog
        open={fastScanOpen && fastScanMode === "ocr"}
        onOpenChange={setFastScanOpen}
        onDetected={fastScanHandler}
        deliveryId={current?.id}
        projectId={current?.project_id}
        continuous={true}
        title="Fast scan · each read = +1"
      />

      {/* Overage confirm */}
      <AlertDialog open={!!pendingOverage} onOpenChange={(o) => { if (!o) setPendingOverage(null); }}>
        <AlertDialogContent data-testid="overage-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-amber-600" /> Overage?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingOverage?.message || "This scan would exceed the expected quantity."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="overage-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={acceptOverage}
              data-testid="overage-accept-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Yes — record as overage
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move to warehouse confirm */}
      <AlertDialog open={!!moveTarget} onOpenChange={(o) => { if (!o) setMoveTarget(null); }}>
        <AlertDialogContent data-testid="move-warehouse-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Warehouse size={20} className="text-blue-800" /> Move to Warehouse Inventory
            </AlertDialogTitle>
            <AlertDialogDescription>
              Pull leftover <span className="font-semibold">{moveTarget?.line?.item_name}</span>{" "}
              (SKU <span className="font-mono">{moveTarget?.line?.sku}</span>) from this off-site
              delivery back into warehouse stock. This lowers the delivery's Received count
              and adds an inventory receive transaction.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Quantity to move</Label>
            <Input
              type="number"
              min="1"
              max={moveTarget?.line?.received_quantity || 1}
              value={moveTarget?.qty || ""}
              onChange={(e) => setMoveTarget((m) => (m ? { ...m, qty: e.target.value } : m))}
              data-testid="move-warehouse-qty"
              className="rounded-sm font-mono"
            />
            <p className="text-xs text-slate-500">
              Currently received on this line: {moveTarget?.line?.received_quantity || 0}
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="move-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doMoveToWarehouse}
              data-testid="move-confirm-btn"
              className="bg-blue-800 hover:bg-blue-900 text-white"
            >
              Move to Warehouse
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
